import { z } from 'zod';
import {
  InternalNetworkAdEntityKindSchema,
  JsonValueSchema,
} from '@terra/shared-schemas-orchestrator';

export {
  ABUSABLE_AD_RELATIONSHIP_TYPES,
  type AbusableAdRelationshipType,
} from './ad-graph-edge-classification';

export const InternalNetworkAdRelationshipTypeSchema = z.string().min(1);

export const AdGraphNodeSchema = z.object({
  id: z.string(),
  kind: InternalNetworkAdEntityKindSchema,
  label: z.string(),
  objectId: z.string(),
  samAccountName: z.string().nullable(),
  distinguishedName: z.string().nullable(),
  isCrownJewel: z.boolean(),
  isOwned: z.boolean().default(false),
  properties: JsonValueSchema.optional(),
});

export const AdGraphLinkSchema = z.object({
  id: z.string(),
  sourceId: z.string(),
  targetId: z.string(),
  relationshipType: InternalNetworkAdRelationshipTypeSchema,
  displayLabel: z.string(),
  isAbusable: z.boolean(),
  label: z.string().optional(),
});

export const AdGraphEdgeSchema = AdGraphLinkSchema;

export const AdGraphNetworkNodePropertiesSchema = z.object({
  source: z.literal('network'),
});

export const AdGraphCrownJewelNodePropertiesSchema = z.object({
  source: z.literal('crown-jewel'),
  crownJewelId: z.string(),
});

export const AdGraphKnownNodePropertiesSchema = z.discriminatedUnion('source', [
  AdGraphNetworkNodePropertiesSchema,
  AdGraphCrownJewelNodePropertiesSchema,
]);

export const AdGraphExplorePresetSchema = z.enum([
  'crown-jewels',
  'domain-admins',
  'domain-controllers',
  'owned-to-targets',
  'kerberoastable-users',
  'as-rep-roastable',
  'dcsync-paths',
  'unconstrained-delegation',
]);

export const AdGraphEntityViewSchema = z.enum([
  'neighborhood',
  'sessions',
  'members',
  'member-of',
  'local-admin-privileges',
  'execution-privileges',
  'inbound-object-control',
  'outbound-object-control',
]);

const MAX_RELATIONSHIP_KIND_SELECTION = 200;

export const AdGraphRelationshipKindsSchema = z.preprocess(
  (value) =>
    typeof value === 'string'
      ? value
          .split(',')
          .map((kind) => kind.trim())
          .filter((kind) => kind.length > 0)
      : value,
  z.array(z.string().min(1).max(64)).max(MAX_RELATIONSHIP_KIND_SELECTION),
);

export const AdGraphQuerySchema = z
  .object({
    mode: z
      .enum(['entry-points', 'full', 'neighborhood', 'preset', 'entity-view'])
      .default('full'),
    centerNodeId: z.string().optional(),
    depth: z.coerce.number().min(1).max(3).optional(),
    preset: AdGraphExplorePresetSchema.optional(),
    entityView: AdGraphEntityViewSchema.optional(),
    relationshipKinds: AdGraphRelationshipKindsSchema.optional(),
  })
  .superRefine((value, ctx) => {
    if (value.mode === 'neighborhood' && !value.centerNodeId) {
      ctx.addIssue({
        code: 'custom',
        message: 'centerNodeId is required for neighborhood mode',
        path: ['centerNodeId'],
      });
    }

    if (value.mode === 'preset' && !value.preset) {
      ctx.addIssue({
        code: 'custom',
        message: 'preset is required for preset mode',
        path: ['preset'],
      });
    }

    if (value.mode === 'entity-view') {
      if (!value.centerNodeId) {
        ctx.addIssue({
          code: 'custom',
          message: 'centerNodeId is required for entity-view mode',
          path: ['centerNodeId'],
        });
      }

      if (!value.entityView) {
        ctx.addIssue({
          code: 'custom',
          message: 'entityView is required for entity-view mode',
          path: ['entityView'],
        });
      }
    }
  });

export const AdGraphResponseSchema = z.object({
  nodes: z.array(AdGraphNodeSchema),
  edges: z.array(AdGraphEdgeSchema),
  truncated: z.boolean(),
  totalCount: z.number(),
  centerResolved: z.boolean().optional(),
});

export type AdGraphNodeDto = z.infer<typeof AdGraphNodeSchema>;
export type AdGraphLinkDto = z.infer<typeof AdGraphLinkSchema>;
export type AdGraphEdgeDto = z.infer<typeof AdGraphEdgeSchema>;
export type AdGraphQueryDto = z.infer<typeof AdGraphQuerySchema>;
export type AdGraphExplorePresetDto = z.infer<
  typeof AdGraphExplorePresetSchema
>;
export type AdGraphEntityViewDto = z.infer<typeof AdGraphEntityViewSchema>;
export type AdGraphResponseDto = z.infer<typeof AdGraphResponseSchema>;
export type AdGraphKnownNodePropertiesDto = z.infer<
  typeof AdGraphKnownNodePropertiesSchema
>;
export type AdGraphCrownJewelNodePropertiesDto = z.infer<
  typeof AdGraphCrownJewelNodePropertiesSchema
>;
export type AdGraphNetworkNodePropertiesDto = z.infer<
  typeof AdGraphNetworkNodePropertiesSchema
>;
